import type { ErrorInfo, JSX, ReactNode } from 'react';
import { Component, createContext, Fragment } from 'react';
import ErrorFallback from '../../components/ErrorFallback';
import { captureComponentError } from '../errors/errorTracking';

export interface ErrorBoundaryState {
  error: Error | null;
}

export interface ErrorFallbackProps {
  error: Error;
  onReset: () => void;
}

export const ErrorBoundaryContext = createContext<ErrorFallbackProps | null>(
  null
);

export interface ErrorBoundaryProps {
  children?: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
  onReset?: () => void;
}

const initialState = { error: null };

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.onReset = this.onReset.bind(this);

    this.state = initialState;
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Capture error to PostHog for tracking
    captureComponentError(error, info.componentStack ?? undefined);

    // Call custom error handler if provided
    this.props.onError?.(error, info);
  }

  onReset(): void {
    this.props.onReset?.();
    this.setState(initialState);
  }

  render(): JSX.Element {
    const { error } = this.state;

    if (error !== null) {
      const contextValue = { error, onReset: this.onReset };

      return (
        <ErrorBoundaryContext.Provider value={contextValue}>
          <ErrorFallback error={error} resetErrorBoundary={this.onReset} />
        </ErrorBoundaryContext.Provider>
      );
    }

    return <Fragment>{this.props.children}</Fragment>;
  }
}

